import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import AutomationPlanModal from './AutomationPlanModal';
import AutomationTimeline from './AutomationTimeline';
import { useCarePlans, useCreateCarePlan, useCreateActionPlan, useUpdateActionPlan, useDeleteActionPlan, useCreateActionTrigger, useUpdateActionTrigger, useDeleteActionTrigger, useActionPlans, useActionTriggers } from '../../utils/reactQueryHooks';
import { fetchActionTriggers } from '../../utils/apiUtils';
import '../../styles/automation.css';

const AutomationTab = ({ actionCard, sensorCard, actType }) => {
    const [activePlan, setActivePlan] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    
    // Get user key for API calls
    const getUserValue = () => {
        try {
            const userValItem = localStorage.getItem('userVal');
            if (userValItem) {
                const parsed = JSON.parse(userValItem);
                return parsed.data;
            }
            return null;
        } catch (error) {
            console.error('Error retrieving user value:', error);
            return null;
        }
    };
    
    const userKey = getUserValue() || 'default';

    // Fetch all care plans (both active and inactive)
    const { data: carePlans = [], isLoading, error } = useCarePlans({});



    // For demo: show execution indicator if there's an active plan
    const demoHasActiveExecution = activePlan && activePlan.isActive;

    // Mutations
    const createCarePlanMutation = useCreateCarePlan();

    const createActionPlanMutation = useCreateActionPlan();
    const updateActionPlanMutation = useUpdateActionPlan();
    const deleteActionPlanMutation = useDeleteActionPlan();
    const createActionTriggerMutation = useCreateActionTrigger();
    const updateActionTriggerMutation = useUpdateActionTrigger();
    const deleteActionTriggerMutation = useDeleteActionTrigger();

    // Fetch action plans and triggers for all care plans
    const { data: actionPlans = [] } = useActionPlans({});
    const { data: actionTriggers = [] } = useActionTriggers({});
    
    console.log('AutomationTab - Re-render with carePlans count:', carePlans.length, 'actionPlans count:', actionPlans.length, 'actionTriggers count:', actionTriggers.length, 'actType:', actType);

    // Transform action plans to match the expected format with real data
    const automationPlans = useMemo(() => {
        // Filter action plans for this specific action (actType and actionCard.macAddress)
        const relevantActionPlans = actionPlans.filter(ap => 
            ap.actuator_type === actType && 
            ap.action_mac_address === actionCard?.macAddress
        );

        return relevantActionPlans.map(actionPlan => {
            // Find the associated care plan
            const associatedCarePlan = carePlans.find(cp => cp.care_plan_id === actionPlan.care_plan_id);
            
            // Find action triggers for this action plan
            const planTriggers = actionTriggers.filter(at => at.action_plan_id === actionPlan.action_plan_id);

            // Transform triggers to events format
            const events = planTriggers.map(trigger => ({
                trigger_id: trigger.trigger_id,
                date: trigger.execution_date,
                time: trigger.execution_time,
                value: trigger.trigger_value,
                month: trigger.month, // Add the month field
                description: trigger.description,
                trigger_type: trigger.trigger_type,
                is_enabled: trigger.is_enabled
            }));

            return {
                id: actionPlan.action_plan_id,
                name: actionPlan.action_name || actionPlan.actuator_description,
                type: actionPlan.plan_type || null,
                recurrence: actionPlan.recurrence_type || null,
                startDate: actionPlan.start_date || '',
                endDate: actionPlan.end_date || '',
                repeatsAnnually: actionPlan.repeats_annually || false,
                isActive: actionPlan.is_active,
                description: actionPlan.actuator_description,
                events: events,
                currentValue: null,
                nextUpdate: null,
                carePlanName: associatedCarePlan?.name || null,
                carePlanId: actionPlan.care_plan_id,
                action_mac_address: actionPlan.action_mac_address // Add for safeguard
            };
        });
    }, [actionPlans, actionTriggers, carePlans, actType, actionCard]);

    // Set active plan when data loads
    useEffect(() => {
        const active = automationPlans.find(plan => plan.isActive);
        setActivePlan(active || null);
    }, [automationPlans]);

    const handleAddPlan = () => {
        console.log('handleAddPlan called');
        setEditingPlan(null);
        setIsModalOpen(true);
    };

    const handleEditPlan = (plan) => {
        console.log('handleEditPlan called with plan:', plan);
        setEditingPlan(plan);
        setIsModalOpen(true);
    };

    const handleDeletePlan = async (planId) => {
        try {
            // Delete action plan instead of care plan
            await deleteActionPlanMutation.mutateAsync(planId);
        } catch (error) {
            console.error('Error deleting action plan:', error);
        }
    };

    const handleSavePlan = async (planData) => {
        console.log('handleSavePlan called with planData:', planData);
        try {
            let carePlanId;
            
            if (editingPlan) {
                // When editing, use the existing care plan ID
                carePlanId = editingPlan.carePlanId;
                console.log('Using existing care plan ID for editing:', carePlanId);
            } else {
                // Handle care plan selection for new plans
                if (planData.carePlanSelection === 'existing') {
                    // Use existing care plan
                    carePlanId = planData.selectedCarePlanId;
                    console.log('Using existing care plan with ID:', carePlanId);
                } else {
                    // Create new care plan
                    const carePlanData = {
                        name: planData.carePlanName,
                        description: planData.description || '',
                        is_active: true // New care plans start as active
                    };

                    console.log('Creating new care plan with data:', carePlanData);
                    const newCarePlan = await createCarePlanMutation.mutateAsync(carePlanData);
                    carePlanId = newCarePlan.care_plan_id || newCarePlan.id;
                    console.log('Created care plan with ID:', carePlanId);
                }
            }

            // Create or update action plan
            let actionPlanId;
            if (editingPlan) {
                // Update existing action plan
                await updateActionPlanMutation.mutateAsync({
                    actionPlanId: editingPlan.id,
                    updateData: {
                        action_name: planData.name,
                        actuator_description: planData.name,
                        plan_type: planData.type,
                        recurrence_type: planData.recurrence,
                        start_date: planData.startDate,
                        end_date: planData.endDate,
                        repeats_annually: planData.repeatsAnnually,
                        is_active: planData.isActive
                        // Note: care_plan_id is not included in update since it shouldn't change
                    }
                });
                actionPlanId = editingPlan.id;
            } else {
                // Create new action plan
                const actionPlanData = {
                    care_plan_id: carePlanId,
                    action_name: planData.name,
                    plan_type: planData.type,
                    recurrence_type: planData.recurrence,
                    start_date: planData.startDate,
                    end_date: planData.endDate,
                    repeats_annually: planData.repeatsAnnually,
                    action_mac_address: actionCard?.macAddress || null,
                    sensor_mac_address: sensorCard?.macAddress || null,
                    actuator_type: actType,
                    actuator_description: planData.name,
                    is_active: true
                };

                console.log('Creating action plan with data:', actionPlanData);
                const actionPlan = await createActionPlanMutation.mutateAsync(actionPlanData);
                actionPlanId = actionPlan.action_plan_id || actionPlan.id;
                console.log('Created action plan with ID:', actionPlanId);
            }

            // Handle action triggers based on whether we're editing or creating
            if (editingPlan) {
                // For editing: Delete, update, or create triggers as needed
                console.log('Editing existing plan - syncing action triggers');
                
                // Get existing triggers for this action plan
                const existingTriggers = await fetchActionTriggers(userKey, { action_plan_id: actionPlanId });
                console.log('Existing triggers:', existingTriggers);
                
                // Create a map of existing triggers by their ID for easier lookup
                const existingTriggersById = new Map();
                existingTriggers.forEach(trigger => {
                    existingTriggersById.set(trigger.trigger_id, trigger);
                });
                
                // Track which triggers we've processed
                const processedTriggerIds = new Set();
                
                // Process each event in the form
                for (let i = 0; i < planData.events.length; i++) {
                    const event = planData.events[i];
                    const triggerOrder = i + 1;
                    
                    // Determine trigger type based on plan type and event data
                    let triggerType;
                    if (planData.type === 'seasonal') {
                        triggerType = 'seasonal';
                    } else if (actType === 'light') {
                        triggerType = 'photoperiod';
                    } else if (actType === 'irrigation') {
                        triggerType = 'watering_frequency';
                    } else if (actType === 'fan' || actType === 'humidity') {
                        triggerType = 'humidity_level';
                    } else {
                        triggerType = 'manual';
                    }

                    if (event.trigger_id) {
                        // This event has an existing trigger ID - update it
                        const existingTrigger = existingTriggersById.get(event.trigger_id);
                        if (existingTrigger) {
                            const updateData = {
                                trigger_order: triggerOrder,
                                trigger_type: triggerType,
                                trigger_value: event.value,
                                execution_date: event.date || new Date().toISOString().split('T')[0],
                                month: event.month || new Date().toLocaleDateString('en-US', { month: 'short' }),
                                execution_time: event.time || '00:00',
                                description: event.description || `${planData.name} event ${i + 1}`,
                                is_enabled: true
                            };
                            
                            console.log(`Updating existing trigger ${existingTrigger.trigger_id} with data:`, updateData);
                            await updateActionTriggerMutation.mutateAsync({
                                triggerId: existingTrigger.trigger_id,
                                updateData
                            });
                            processedTriggerIds.add(existingTrigger.trigger_id);
                        }
                    } else {
                        // This is a new event - create a new trigger
                        const triggerData = {
                            action_plan_id: actionPlanId,
                            trigger_order: triggerOrder,
                            trigger_type: triggerType,
                            trigger_value: event.value,
                            execution_date: event.date || new Date().toISOString().split('T')[0],
                            month: event.month || new Date().toLocaleDateString('en-US', { month: 'short' }),
                            execution_time: event.time || '00:00',
                            description: event.description || `${planData.name} event ${i + 1}`,
                            is_enabled: true
                        };
                        
                        console.log(`Creating new trigger ${triggerOrder} with data:`, triggerData);
                        await createActionTriggerMutation.mutateAsync(triggerData);
                    }
                }
                
                // Delete any triggers that weren't processed (i.e., were removed from the form)
                for (const trigger of existingTriggers) {
                    if (!processedTriggerIds.has(trigger.trigger_id)) {
                        console.log(`Deleting trigger ${trigger.trigger_id} (order ${trigger.trigger_order}) - no longer in form`);
                        await deleteActionTriggerMutation.mutateAsync(trigger.trigger_id);
                    }
                }
            } else {
                // For new plans: Create all triggers
                console.log('Creating new plan - creating action triggers for events:', planData.events);
                for (let i = 0; i < planData.events.length; i++) {
                    const event = planData.events[i];
                    
                    // Determine trigger type based on plan type and event data
                    let triggerType;
                    if (planData.type === 'seasonal') {
                        triggerType = 'seasonal';
                    } else if (actType === 'light') {
                        triggerType = 'photoperiod';
                    } else if (actType === 'irrigation') {
                        triggerType = 'watering_frequency';
                    } else if (actType === 'fan' || actType === 'humidity') {
                        triggerType = 'humidity_level';
                    } else {
                        triggerType = 'manual';
                    }

                    const triggerData = {
                        action_plan_id: actionPlanId,
                        trigger_order: i + 1,
                        trigger_type: triggerType,
                        trigger_value: event.value,
                        execution_date: event.date || new Date().toISOString().split('T')[0],
                        month: event.month || new Date().toLocaleDateString('en-US', { month: 'short' }),
                        execution_time: event.time || '00:00',
                        description: event.description || `${planData.name} event ${i + 1}`,
                        is_enabled: true
                    };

                    console.log(`Creating action trigger ${i + 1} with data:`, triggerData);
                    await createActionTriggerMutation.mutateAsync(triggerData);
                }
            }

            console.log('Successfully created complete automation plan');
            setIsModalOpen(false);
            setEditingPlan(null);
        } catch (error) {
            console.error('Error saving automation plan:', error);
            // You might want to show a toast notification here
        }
    };

    const handleTogglePlan = async (planId) => {
        try {
            const plan = automationPlans.find(p => p.id === planId);
            if (plan) {
                const newActiveState = !plan.isActive;
                
                // If trying to activate a plan, check if another plan is already active for this action
                if (newActiveState) {
                    const otherActivePlans = automationPlans.filter(p => 
                        p.id !== planId && 
                        p.isActive && 
                        p.action_mac_address === plan.action_mac_address
                    );
                    
                    if (otherActivePlans.length > 0) {
                        const otherPlan = otherActivePlans[0];
                        alert(`Cannot activate this plan while "${otherPlan.name}" is active. Please deactivate the other plan first.`);
                        return;
                    }
                }
                
                await updateActionPlanMutation.mutateAsync({
                    actionPlanId: planId,
                    updateData: {
                        is_active: newActiveState
                    }
                });
            } else {
                console.error('Plan not found with ID:', planId);
            }
        } catch (error) {
            console.error('Error toggling action plan:', error);
        }
    };

    if (isLoading) {
        return (
            <div className="automation-tab">
                <div className="loading-state">
                    <i className="ri-loader-4-line"></i>
                    <p>Loading automation plans...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="automation-tab">
                <div className="error-state">
                    <i className="ri-error-warning-line"></i>
                    <p>Error loading automation plans</p>
                    <small>{error.message}</small>
                </div>
            </div>
        );
    }

    return (
        <div className="automation-tab">
            {/* Plans List */}
            <div className="automation-plans">
                {automationPlans.length === 0 ? (
                    <div className="no-plans-state">
                        <div className="no-plans-icon">
                            <i className="ri-time-line"></i>
                        </div>
                        <h4>No automation plans yet</h4>
                        <p>Create your first automation plan to schedule changes automatically</p>
                        <button 
                            className="create-first-plan-btn"
                            onClick={handleAddPlan}
                        >
                            <i className="ri-add-line"></i>
                            Create First Plan
                        </button>
                    </div>
                ) : (
                    <div className="plans-list">
                        <div className="plans-header">
                            <h4>Automation Plans</h4>
                            <button 
                                className="add-plan-btn"
                                onClick={handleAddPlan}
                                title="Add new automation plan"
                            >
                                <i className="ri-add-line"></i>
                                Add Plan
                            </button>
                        </div>
                        {automationPlans.map(plan => (
                            <div 
                                key={plan.id} 
                                className={`plan-card ${plan.isActive ? 'active' : ''}`}
                            >
                                <div className="plan-header">
                                    <div className="plan-info">
                                        <h5>{plan.name}</h5>
                                        <span className="plan-type">
                                            {plan.type === 'seasonal' ? '🌱 Seasonal' : '🕐 Daily'}
                                        </span>
                                    </div>
                                    <div className="plan-actions">
                                        <button 
                                            className={`toggle-plan-btn ${plan.isActive ? 'active' : ''}`}
                                            onClick={() => handleTogglePlan(plan.id)}
                                            title={plan.isActive ? 'Deactivate' : 'Activate'}
                                        >
                                            <i className={`ri-${plan.isActive ? 'pause' : 'play'}-line`}></i>
                                        </button>
                                        <button 
                                            className="edit-plan-btn"
                                            onClick={() => handleEditPlan(plan)}
                                            title="Edit"
                                        >
                                            <i className="ri-edit-line"></i>
                                        </button>
                                        <button 
                                            className="delete-plan-btn"
                                            onClick={() => handleDeletePlan(plan.id)}
                                            title="Delete"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="plan-details">
                                    <span className="care-plan-indicator">
                                        <i className="ri-plant-line"></i>
                                        {plan.carePlanName}
                                    </span>
                                    <span className="plan-recurrence">
                                        {plan.recurrence === 'monthly' ? 'Monthly' : 'Daily'} schedule
                                    </span>
                                    {plan.repeatsAnnually && (
                                        <span className="plan-annual">
                                            <i className="ri-refresh-line"></i>
                                            Repeats annually
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Timeline View - Only show for active plans */}
            {automationPlans.filter(plan => plan.isActive).length > 0 && (
                <AutomationTimeline 
                    plans={automationPlans}
                    actionType={actType}
                />
            )}

            {/* Modal */}
            <AutomationPlanModal
                isVisible={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingPlan(null);
                }}
                onSave={handleSavePlan}
                plan={editingPlan}
                actionType={actType}
                actionCard={actionCard}
                sensorCard={sensorCard}
            />
        </div>
    );
};

AutomationTab.propTypes = {
    actionCard: PropTypes.object.isRequired,
    sensorCard: PropTypes.object.isRequired,
    actType: PropTypes.string.isRequired
};

export default AutomationTab; 